import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
// eslint-disable-next-line no-unused-vars
import Chart from 'chart.js/auto';
import Loader from '../../../Components/utils/Loader';
import { CSVLink } from 'react-csv';
import { FiDownload } from 'react-icons/fi';
import { exportToPDF } from '../../../utils/exportUtils';

const API = process.env.REACT_APP_Backend_Host_Name;
const COLORS = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ec4899','#8b5cf6','#ef4444','#14b8a6','#f97316','#64748b'];

function AnalyticsUsers({ query }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem('adminToken');

    useEffect(() => {
        setLoading(true);
        axios.get(`${API}/api/v1/settings/admin/analytics/users?${query}`, { headers: { Authorization: `Admin ${token}` } })
            .then(res => setData(res.data))
            .finally(() => setLoading(false));
    }, [query, token]);

    if (loading) return <Loader type="simpleMax" />;
    if (!data) return null;

    const chartOpts = (title) => ({
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: { legend: { position: 'bottom', labels: { padding: 15, usePointStyle: true, pointStyle: 'circle', font: { size: 11 } } }, title: { display: !!title, text: title, font: { size: 13, weight: '500' } } },
        scales: { x: { grid: { display: false }, ticks: { font: { size: 10 }, maxTicksLimit: 12 } }, y: { grid: { color: '#f0f0f0' }, ticks: { font: { size: 10 } }, beginAtZero: true } }
    });

    const doughnutOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { padding: 12, usePointStyle: true, pointStyle: 'circle', font: { size: 11 } } } }, cutout: '65%' };

    const regChart = {
        labels: data.registrations.map(r => r.date),
        datasets: [
            { label: 'All Users', data: data.registrations.map(r => r.count), borderColor: '#6366f1', backgroundColor: 'rgba(99,102,241,0.08)', fill: true, tension: 0.4, pointRadius: 2 },
            { label: 'Buyers', data: data.buyerRegistrations.map(r => r.count), borderColor: '#0ea5e9', backgroundColor: 'rgba(14,165,233,0.08)', fill: true, tension: 0.4, pointRadius: 2 },
            { label: 'Sellers', data: data.sellerRegistrations.map(r => r.count), borderColor: '#10b981', backgroundColor: 'rgba(16,185,129,0.08)', fill: true, tension: 0.4, pointRadius: 2 },
        ]
    };

    const activeBlockedChart = {
        labels: ['Active', 'Blocked'],
        datasets: [{ data: [data.activeVsBlocked.active, data.activeVsBlocked.blocked], backgroundColor: ['#10b981', '#ef4444'], borderWidth: 0 }]
    };

    const freeVsPaidChart = {
        labels: ['Free', 'Paid'],
        datasets: [{ data: [data.freeVsPaid.free, data.freeVsPaid.paid], backgroundColor: ['#64748b', '#6366f1'], borderWidth: 0 }]
    };

    const countryChart = {
        labels: data.countryDistribution.map(c => c.country),
        datasets: [{ label: 'Users', data: data.countryDistribution.map(c => c.count), backgroundColor: COLORS.slice(0, data.countryDistribution.length), borderRadius: 4, barThickness: 20 }]
    };

    const csvData = data ? [
        { Metric: 'Total Active Users', Value: data.activeVsBlocked.active, Details: '', Extra: '' },
        { Metric: 'Total Blocked Users', Value: data.activeVsBlocked.blocked, Details: '', Extra: '' },
        { Metric: 'Total Free Sellers', Value: data.freeVsPaid.free, Details: '', Extra: '' },
        { Metric: 'Total Paid Sellers', Value: data.freeVsPaid.paid, Details: '', Extra: '' },
        { Metric: '', Value: '', Details: '', Extra: '' },
        { Metric: 'COUNTRY DISTRIBUTION', Value: '', Details: '', Extra: '' },
        ...data.countryDistribution.map(c => ({ Metric: c.country, Value: c.count, Details: '', Extra: '' })),
        { Metric: '', Value: '', Details: '', Extra: '' },
        { Metric: 'REGISTRATIONS OVER TIME', Value: '', Details: '', Extra: '' },
        { Metric: 'Date', Value: 'Total Registrations', Details: 'Buyers', Extra: 'Sellers' },
        ...data.registrations.map((r, i) => ({
            Metric: r.date,
            Value: r.count,
            Details: data.buyerRegistrations[i]?.count || 0,
            Extra: data.sellerRegistrations[i]?.count || 0
        }))
    ] : [];

    return (
        <div className="analyticsUsersTab" id="analytics-users-report">
            <div className="analyticsExportBar">
                <CSVLink data={csvData} filename="users_growth_report.csv" className="exportBtn csvBtn">
                    <FiDownload /> Export CSV
                </CSVLink>
                <button onClick={() => exportToPDF('analytics-users-report', 'users_growth_report')} className="exportBtn pdfBtn">
                    <FiDownload /> Export PDF
                </button>
            </div>

            <div className="analyticsChartCard">
                <h3 className="chartCardTitle">User Registrations Over Time</h3>
                <div className="chartContainer" style={{ height: 300 }}><Line data={regChart} options={chartOpts()} /></div>
            </div>

            <div className="analyticsGridRow three">
                <div className="analyticsChartCard">
                    <h3 className="chartCardTitle">Active vs Blocked</h3>
                    <div className="chartContainer" style={{ height: 220 }}><Doughnut data={activeBlockedChart} options={doughnutOpts} /></div>
                    <div className="chartLegendSummary">
                        <span className="legendDot" style={{ background: '#10b981' }}></span> {data.activeVsBlocked.active} Active
                        <span className="legendDot" style={{ background: '#ef4444', marginLeft: 12 }}></span> {data.activeVsBlocked.blocked} Blocked
                    </div>
                </div>
                <div className="analyticsChartCard">
                    <h3 className="chartCardTitle">Free vs Paid Sellers</h3>
                    <div className="chartContainer" style={{ height: 220 }}><Doughnut data={freeVsPaidChart} options={doughnutOpts} /></div>
                    <div className="chartLegendSummary">
                        <span className="legendDot" style={{ background: '#64748b' }}></span> {data.freeVsPaid.free} Free
                        <span className="legendDot" style={{ background: '#6366f1', marginLeft: 12 }}></span> {data.freeVsPaid.paid} Paid
                    </div>
                </div>
            </div>

            <div className="analyticsChartCard">
                <h3 className="chartCardTitle">Users by Country (Top 10)</h3>
                <div className="chartContainer" style={{ height: 280 }}><Bar data={countryChart} options={chartOpts()} /></div>
            </div>

            {data.b2bSellerStatus.length > 0 && (
                <div className="analyticsChartCard">
                    <h3 className="chartCardTitle">B2B Seller Applications</h3>
                    <div className="analyticsSimpleStats">
                        {data.b2bSellerStatus.map((s, i) => (
                            <div key={i} className="simpleStat">
                                <div className="simpleStatValue">{s.count}</div>
                                <div className="simpleStatLabel" style={{ textTransform: 'capitalize' }}>{s.status}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default AnalyticsUsers;
